import React, { useEffect, useState, useMemo } from "react";
import Loader from "../../Shared/Loader";

import "../../../css/CentersTable.css";
import AddCenterModal from "./AddCenterModal";
import ViewCenterModal from "./ViewCenterModal";
import api from "../../../api/axiosConfig";

// ---------- Types (No changes) ----------
interface WorkingTiming {
  day: string;
  startTime: string;
  endTime: string;
}

interface Holiday {
  holidayName: string;
  day: string;
  date: string;
}

interface Center {
  _id: string;
  name: string;
  description?: string;
  centerType: string;
  city: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  disabledOn?: string;
  addressLine1?: string;
  addressLine2?: string;
  latitude?: number;
  longitude?: number;
  contactEmail?: string;
  contactPhone?: string;
  workingTimings?: WorkingTiming[];
  holidays?: Holiday[];
}

// ---------- Component ----------
const QavachCenterTable: React.FC = () => {
  const [centers, setCenters] = useState<Center[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState<Center | null>(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  // 🔽 1. State for filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchCenters = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const res = await api.center_api.get(
        // We fetch all centers and handle pagination/filtering on the client
        "/api/v1/centers?&type=QAVACH CENTER",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCenters(res.data.data || []);
    } catch (err) {
      console.error("Error fetching centers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCenters();
  }, []);

  // 🔽 2. Memoized filtering logic
  const filteredCenters = useMemo(() => {
    return centers
      .filter(c => {
        // Search by center name or city
        const searchMatch =
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.city.toLowerCase().includes(searchTerm.toLowerCase());
        return searchMatch;
      })
      .filter(c => {
        // Filter by status
        if (statusFilter === "All") return true;
        const isActive = statusFilter === "Active";
        return c.isActive === isActive;
      })
      .filter(c => {
        // Filter by category
        if (categoryFilter === "All") return true;
        return c.centerType.trim() === categoryFilter.trim();
      });
  }, [centers, searchTerm, statusFilter, categoryFilter]);
  
  // 🔽 Get unique categories for the dropdown
  const uniqueCategories = useMemo(() => {
      const categories = new Set(centers.map(c => c.centerType));
      return ["All Types", ...Array.from(categories)];
  }, [centers]);


  // 🔽 3. Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter]);

  // 🔽 4. Pagination Logic
  const totalPages = Math.ceil(filteredCenters.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCentersToDisplay = filteredCenters.slice(startIndex, endIndex);

  return (
    <div className="qavach-container">
      <div className="top-bar">
        {/* --- Controlled Inputs --- */}
        <div className="global-filters-container">
          <div className="global-search-wrapper">
            <svg
              className="global-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search by name or city..."
              className="global-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="global-select-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Disabled">Disabled</option>
          </select>
          <select 
            className="global-select-input"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {uniqueCategories.map(cat => (
              <option key={cat} value={cat === "All Types" ? "All" : cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <button className="add-btn" onClick={() => setOpenModal(true)}>
          + Add Center
        </button>
      </div>

      <div className="table-header"><h2>Centers</h2></div>

      <table className="centers-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Center Name</th>
            <th>Center Category</th>
            <th>City</th>
            <th>Status</th>
            <th>Created On</th>
            <th>Disabled On</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={7}><Loader inline text="Loading Centers..." /></td></tr>
          ) : currentCentersToDisplay.length > 0 ? (
            currentCentersToDisplay.map((c, index) => (
              <tr
                key={c._id}
                onClick={() => {
                  setSelectedCenter(c);
                  setOpenViewModal(true);
                }}
                style={{ cursor: "pointer" }}
              >
                <td>{startIndex + index + 1}</td>
                <td className="name-bold">{c.name}</td>
                <td><span className="badge type" style={{color:'black'}}>{c.centerType}</span></td>
                <td>{c.city}</td>
                <td>
                  <span className={`roles-page-status ${c.isActive ? "roles-page-status-active" : "roles-page-status-inactive"}`}>
                    {c.isActive ? "Active" : "Disabled"}
                  </span>
                </td>
                <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                <td>{c.disabledOn ? new Date(c.disabledOn).toLocaleDateString() : "-"}</td>
              </tr>
            ))
          ) : (
            <tr><td colSpan={7} style={{ textAlign: "center" }}>No centers match the current filters.</td></tr>
          )}
        </tbody>
      </table>

      {/* Modals */}
      <ViewCenterModal isOpen={openViewModal} onClose={() => setOpenViewModal(false)} center={selectedCenter} onUpdate={fetchCenters} />
      <AddCenterModal isOpen={openModal} onClose={() => setOpenModal(false)} onCreate={fetchCenters} />

      {/* 🔽 5. Dynamic Pagination Footer */}
      {totalPages > 0 && (
        <div className="pagination-footer">
          <p>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredCenters.length)} of {filteredCenters.length} results
          </p>
          <div className="pagination">
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
              <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)} className={currentPage === pageNumber ? "active" : ""}>
                {pageNumber}
              </button>
            ))}
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QavachCenterTable;